import { useCallback, useRef, useEffect, useState } from 'react';
import type { Editor } from '@tiptap/react';
import { traceRTEDocumentWhitespace, type RTEDocument } from '@/components/blog-templates';

interface AutoSaveOptions {
  editor: Editor | null;
  /** Save callback receives JSON, HTML, and word count for flexibility */
  onSave: (json: RTEDocument, html: string, wordCount: number) => Promise<void>;
  debounceMs?: number;
  enabled?: boolean;
}

interface AutoSaveReturn {
  isSaving: boolean;
  lastSaved: Date | null;
  saveNow: () => Promise<void>;
  error: Error | null;
  hasUnsavedChanges: boolean;
}

export function useTipTapAutoSave({
  editor,
  onSave,
  debounceMs = 2000,
  enabled = true,
}: AutoSaveOptions): AutoSaveReturn {
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingSaveRef = useRef(false);
  const savingRef = useRef(false);
  const isMountedRef = useRef(true);
  const onSaveRef = useRef(onSave);

  // Keep onSave ref updated to avoid stale closure issues
  useEffect(() => {
    onSaveRef.current = onSave;
  }, [onSave]);

  // Track mounted state
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const saveContent = useCallback(async () => {
    if (!editor || savingRef.current || !isMountedRef.current) {
      if (editor && !savingRef.current) {
        pendingSaveRef.current = true;
      }
      return;
    }

    try {
      savingRef.current = true;
      if (isMountedRef.current) {
        setIsSaving(true);
        setError(null);
      }

      // Get JSON (primary), HTML (for backward compatibility), and word count
      const json = editor.getJSON() as RTEDocument;
      const html = editor.getHTML();
      const wordCount = editor.storage.characterCount?.words?.() ?? 0;
      traceRTEDocumentWhitespace('TipTapAutoSave:before_onSave', json, {
        html_length: html.length,
        word_count: wordCount,
      });

      await onSaveRef.current(json, html, wordCount);

      if (isMountedRef.current) {
        setLastSaved(new Date());
        setHasUnsavedChanges(false);
      }

      // Handle any pending saves that came in while saving
      if (pendingSaveRef.current && isMountedRef.current) {
        pendingSaveRef.current = false;
        // Schedule another save
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        timeoutRef.current = setTimeout(saveContent, debounceMs);
      }
    } catch (err) {
      if (isMountedRef.current) {
        const saveError = err instanceof Error ? err : new Error(String(err));
        setError(saveError);
        console.error('Auto-save failed:', err);
      }
    } finally {
      savingRef.current = false;
      if (isMountedRef.current) {
        setIsSaving(false);
      }
    }
  }, [editor, debounceMs]);

  // Set up debounced auto-save on content changes
  useEffect(() => {
    if (!editor || !enabled) return;

    const handleUpdate = () => {
      setHasUnsavedChanges(true);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(saveContent, debounceMs);
    };

    editor.on('update', handleUpdate);

    return () => {
      editor.off('update', handleUpdate);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [editor, enabled, saveContent, debounceMs]);

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, []);

  // Manual save function
  const saveNow = useCallback(async () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    await saveContent();
  }, [saveContent]);

  return {
    isSaving,
    lastSaved,
    saveNow,
    error,
    hasUnsavedChanges,
  };
}

export default useTipTapAutoSave;
